import { lazy } from 'react';
import { LuArrowDownAZ, LuClock, LuHash, LuTag } from 'react-icons/lu';
import { appFixCategories } from 'shared/apps/appFixCategories.js';

// Shared building blocks for the App Fixer page components. Kept in one module
// so the style objects and label helpers have a single, stable identity and are
// never re-created per render.

export const DEFECT_ACTION_NAME_APP_VERSION = 'draft';
export const DEFAULT_DEFECT_STATUSES = ['open', 'fixing', 'proposed', 'blocked'];
export const DEFECT_FETCH_LIMIT = 200;

export const Markdown = lazy(() => import('react-markdown'));

export const statusVariants = { open: 'warning', fixing: 'info', proposed: 'info', resolved: 'success', dismissed: 'default', blocked: 'error' };
export const sourceLabels = { detector: 'Detector', admin: 'Admin', 'cirra-agent': 'Cirra Agent', 'thread-miner': 'Thread Miner' };

// Mirror the diffs-viewer modal: an operation-colored icon, the field path, then
// the old value struck through and the new value in green.
export const operationBadgeColors = {
  add: { color: '#15803d', backgroundColor: '#dafbe1' },
  delete: { color: '#b91c1c', backgroundColor: '#ffebe9' },
  change: { color: '#1d4ed8', backgroundColor: '#eff6ff' }
};
export const operationBadgeStyle = { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 4, flexShrink: 0 };

export const statusFilters = [
  { value: 'all', label: 'Default' },
  { value: 'open', label: 'Open' },
  { value: 'fixing', label: 'Fixing' },
  { value: 'proposed', label: 'Proposed' },
  { value: 'blocked', label: 'Blocked' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'dismissed', label: 'Dismissed' }
];
export const defectStatusOptions = statusFilters.filter((filter) => filter.value !== 'all');

export const defectSortOptions = [
  { value: 'lastObservedOn', label: 'Time Ago', icon: LuClock },
  { value: 'appName', label: 'App Name', icon: LuArrowDownAZ },
  { value: 'occurrenceCount', label: 'Occurrences', icon: LuHash },
  { value: 'source', label: 'Source', icon: LuTag }
].map((option) => ({ ...option, iconProps: { size: 12, className: 'light-medium' } }));

export const sectionTitleStyle = { fontSize: 13, fontWeight: 500, color: '#111827', margin: '0 0 4px' };
export const sectionSubtitleStyle = { fontSize: 12, color: '#6b7280', margin: '0 0 12px' };
export const cardStyle = { border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 12, backgroundColor: '#fff' };
export const cardHeaderStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer' };
export const monoStyle = { fontFamily: 'var(--font-mono, ui-monospace, SFMono-Regular, Menlo, monospace)', fontSize: 12 };
export const labelStyle = { fontSize: 11, fontWeight: 500, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' };
export const controlsRowStyle = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 };
export const emptyStyle = { fontSize: 13, color: '#9ca3af', padding: '12px 0' };
export const rowTitleStyle = { fontWeight: 500, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
export const rowActionNameStyle = { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
export const rowMetaStyle = { fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' };
// Every defect row insets its content by 9px, the same distance the search icon
// sits inside the search box above, so the app icon lines up with the search
// icon. The current row adds only the highlight on top.
export const rowInsetStyle = { padding: '0 9px' };
export const currentRowStyle = { ...rowInsetStyle, backgroundColor: 'rgba(37, 99, 235, 0.08)', borderRadius: 8 };
export const codeBlockStyle = { ...monoStyle, whiteSpace: 'pre-wrap', wordBreak: 'break-word', backgroundColor: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', margin: 0, maxHeight: 260, overflow: 'auto' };
export const diffRowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f0f0f0' };
const diffValuePillBase = { ...monoStyle, padding: '2px 6px', borderRadius: 4, maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
export const diffPreviousPillStyle = { ...diffValuePillBase, backgroundColor: '#ffebe9', color: 'rgba(185, 28, 28, 0.9)', textDecoration: 'line-through' };
export const diffNewPillStyle = { ...diffValuePillBase, backgroundColor: '#dafbe1', color: '#166534' };
export const evidenceLinkStyle = { color: '#2563eb', textDecoration: 'none', fontSize: 12, wordBreak: 'break-all', cursor: 'pointer' };
export const inlineCodeStyle = { ...monoStyle, backgroundColor: '#f3f4f6', borderRadius: 4, padding: '1px 4px' };

// Dollar cost of the AI work on a defect. Small runs cost cents, so show four
// decimals under a cent and two above. Returns null when there is nothing to
// show, so callers hide the element.
export const formatAppFixCost = (costUsd) => {
  const amount = Number(costUsd);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }
  return amount < 0.01 ? `$${amount.toFixed(4)}` : `$${amount.toFixed(2)}`;
};

export const getCategoryLabel = (key) => appFixCategories[key]?.label || key || 'Unknown';
export const getStatusLabel = (status) => defectStatusOptions.find((filter) => filter.value === status)?.label || status;
export const getSourceLabel = (source) => sourceLabels[source] || source || 'Unknown';

export const formatValue = (value) => {
  if (value === undefined) {
    return '(none)';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value, null, 2);
};

export const formatLastObservedDate = (value) => {
  const dt = new Date(value);
  if (!value || Number.isNaN(dt.getTime())) {
    return null;
  }
  return dt.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

export const compareDefectsAscending = (a, b, sortBy) => {
  if (sortBy === 'appName') {
    return a.appName.localeCompare(b.appName);
  }
  if (sortBy === 'occurrenceCount') {
    return (a.occurrenceCount || 0) - (b.occurrenceCount || 0);
  }
  if (sortBy === 'source') {
    return getSourceLabel(a.source).localeCompare(getSourceLabel(b.source));
  }
  return new Date(a.lastObservedOn).getTime() - new Date(b.lastObservedOn).getTime();
};

export const describeApproveError = (err) => {
  const code = err?.code;
  if (code === 'APP_FIX_PROPOSAL_BASE_STALE') {
    return 'The published version moved since this fix was computed. Reject it with a comment to relaunch the agent against the current base.';
  }
  if (code === 'APP_FIX_DRAFT_CONFLICTS') {
    const conflicts = err?.data?.conflicts;
    const paths = Array.isArray(conflicts) ? conflicts.map((c) => c?.path || c).filter(Boolean) : [];
    const suffix = paths.length ? ` Conflicting paths: ${paths.join(', ')}.` : '';
    return `The app draft has edits on paths this fix touches. Resolve the draft first, then approve.${suffix}`;
  }
  return err?.message || 'Could not approve this fix.';
};

// Render the agent's human notes. Machine records (raw runs, validation counts)
// are stored on the proposal but never shown here.
export const notesMarkdownComponents = {
  p: ({ children }) => <p style={{ margin: '0 0 8px', fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{children}</p>,
  ul: ({ children }) => <ul style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ margin: '0 0 8px', paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.55 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 2 }}>{children}</li>,
  strong: ({ children }) => <strong style={{ fontWeight: 500 }}>{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={evidenceLinkStyle}>
      {children}
    </a>
  ),
  pre: ({ children }) => <pre style={{ ...codeBlockStyle, marginTop: 4 }}>{children}</pre>,
  code: ({ className, children }) => {
    const isBlock = typeof className === 'string' && className.includes('language-');
    return isBlock ? <code style={monoStyle}>{children}</code> : <code style={inlineCodeStyle}>{children}</code>;
  }
};

// The summary is one or two sentences. It only needs inline formatting
// (identifiers in backticks); paragraphs carry no bottom margin because the
// wrapper div supplies the spacing.
export const summaryMarkdownComponents = {
  p: ({ children }) => <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>{children}</p>,
  strong: ({ children }) => <strong style={{ fontWeight: 500 }}>{children}</strong>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={evidenceLinkStyle}>
      {children}
    </a>
  ),
  code: ({ children }) => <code style={inlineCodeStyle}>{children}</code>
};
